/**
 * TanStack Query v5 client + the single source of truth for cache keys.
 *
 * Every hook in `src/features/*` builds its key from `queryKeys` — never inline arrays.
 * That is what makes invalidation from mutations and from the WebSocket (`lib/ws.ts`) reliable.
 */

import { QueryClient, type QueryKey } from '@tanstack/react-query';

import { ApiError } from '@/lib/api';
import type {
  BotFilters,
  CandleTimeframe,
  DealFilters,
  EventFilters,
  MarketType,
  UUID,
} from '@/types/api';

/** Normalise a filter object into a stable, serialisable key fragment. */
function keyFilters<T extends object>(filters: T | undefined): Record<string, unknown> {
  if (!filters) return {};
  const out: Record<string, unknown> = {};
  const source = filters as Record<string, unknown>;
  for (const key of Object.keys(source).sort()) {
    const value = source[key];
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  exchangeAccounts: {
    all: ['exchange-accounts'] as const,
    list: () => [...queryKeys.exchangeAccounts.all, 'list'] as const,
    detail: (id: UUID) => [...queryKeys.exchangeAccounts.all, 'detail', id] as const,
    balances: (accountId: UUID) =>
      [...queryKeys.exchangeAccounts.all, 'balances', accountId] as const,
    balanceHistory: (accountId: UUID, days: number) =>
      [...queryKeys.exchangeAccounts.all, 'balance-history', accountId, days] as const,
    balanceSummary: () => [...queryKeys.exchangeAccounts.all, 'balance-summary'] as const,
  },

  market: {
    all: ['market'] as const,
    // FUTURES.md §6: the symbol set differs per market type, so it is part of the cache key.
    symbols: (accountId: UUID | undefined, marketType: MarketType = 'spot') =>
      [...queryKeys.market.all, 'symbols', accountId ?? null, marketType] as const,
    ticker: (accountId: UUID | undefined, symbol: string | undefined, marketType: MarketType = 'spot') =>
      [...queryKeys.market.all, 'ticker', accountId ?? null, symbol ?? null, marketType] as const,
    candles: (
      accountId: UUID | undefined,
      symbol: string | undefined,
      timeframe: CandleTimeframe,
      limit: number,
    ) =>
      [
        ...queryKeys.market.all,
        'candles',
        accountId ?? null,
        symbol ?? null,
        timeframe,
        limit,
      ] as const,
  },

  portfolio: {
    all: ['portfolio'] as const,
    // The market type is part of the key for the same reason it is on the symbol list: a
    // futures account and a spot one hold entirely different things (§6.8).
    snapshot: (accountId: UUID | undefined, marketType: MarketType = 'futures') =>
      [...queryKeys.portfolio.all, 'snapshot', accountId ?? null, marketType] as const,
  },

  bots: {
    all: ['bots'] as const,
    lists: () => [...queryKeys.bots.all, 'list'] as const,
    list: (filters?: BotFilters) => [...queryKeys.bots.lists(), keyFilters(filters)] as const,
    details: () => [...queryKeys.bots.all, 'detail'] as const,
    detail: (id: UUID) => [...queryKeys.bots.details(), id] as const,
    stats: (id: UUID) => [...queryKeys.bots.all, 'stats', id] as const,
    pairs: (id: UUID) => [...queryKeys.bots.all, 'pairs', id] as const,
    hedge: (id: UUID) => [...queryKeys.bots.all, 'hedge', id] as const,
  },

  deals: {
    all: ['deals'] as const,
    lists: () => [...queryKeys.deals.all, 'list'] as const,
    list: (filters?: DealFilters) => [...queryKeys.deals.lists(), keyFilters(filters)] as const,
    details: () => [...queryKeys.deals.all, 'detail'] as const,
    detail: (id: UUID) => [...queryKeys.deals.details(), id] as const,
    orders: (id: UUID) => [...queryKeys.deals.all, 'orders', id] as const,
    hedge: (id: UUID) => [...queryKeys.deals.all, 'hedge', id] as const,
  },

  stats: {
    all: ['stats'] as const,
    overview: () => [...queryKeys.stats.all, 'overview'] as const,
    accounts: () => [...queryKeys.stats.all, 'accounts'] as const,
    pnlSeries: (days: number, botId?: UUID) =>
      [...queryKeys.stats.all, 'pnl-series', days, botId ?? null] as const,
    performance: (days: number) => [...queryKeys.stats.all, 'performance', days] as const,
  },

  events: {
    all: ['events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (filters?: EventFilters) => [...queryKeys.events.lists(), keyFilters(filters)] as const,
    attention: () => [...queryKeys.events.all, 'attention'] as const,
  },
} as const;

/** Convenience for `invalidateQueries({ queryKey })` call sites that need a plain QueryKey. */
export type AppQueryKey = QueryKey;

/**
 * Retry policy: one retry, and never for 4xx — a 404 or a 422 will not fix itself.
 * Network errors (status 0) are retried, since those are usually a blip.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  if (ApiError.is(error) && error.isClientError) return false;
  return true;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        throwOnError: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/** App-wide singleton. Tests should build their own with {@link createQueryClient}. */
export const queryClient = createQueryClient();
